// The way onto the tour from anywhere (public/tour/).
//
// A visitor can only join from a page the tour is on: the button appears when
// the guide is heard in that page's room, and a page the tour is not on hears
// nothing. So a link handed to someone — a QR code on a caption panel, a URL
// in a message — cannot name a space; by the time it is opened the tour has
// moved. This page names none. It records that the visitor means to be on
// the tour and sends them to whichever space the tour is in now — computed
// the same way the guide computes it, from the same clock AND the same map
// (see below), the same way icc/lounge-east-hyper-tour/* send a screen — and
// the page they land on joins them the moment it hears the guide.
//
// That this page computes the tour's position at all is interim. The canon is
// the guide, not the clock (decision-2026-09-11-planning-guide-is-an-agent ①),
// and once the guide is a process of its own under ② the way in is to ask the
// room, or the guide, where the tour is — not to run a second copy of the walk
// here and hope the two agree.
//
// sessionStorage, like the screen pages: it survives exactly this one
// navigation in this one tab, and a URL that carried it would carry the next
// person too.
import { TourProgram } from './TourProgram.js';
import { HYPER_TOUR_PROGRAM, stageForSpace } from './program.js';
import { loadMapSheet, mapSheet } from './hyper-map.js';
import { waypointsBetween } from './route-through.js';
import { askTheRoom } from './ask-the-room.js';
import { JOINING_OPEN } from './visitor.js';
import { withPageLang } from './carry-lang.js';
import { markCrossing } from './crossing-mark.js';

const FOLLOWING_KEY = 'hyper-tour-following';
const PARTICIPATING_KEY = 'hyper-tour-participating';

// There is no following without joining
// (decision-2026-10-02-planning-no-follow-only-mode): this page is the way
// into the flock, and following is only how being in it moves the camera.
// Where the build keeps joining closed (TOUR_JOIN) there is no way on at
// all, and the tour is watched on /hypertour/.
if ( !JOINING_OPEN ) window.location.replace( withPageLang( '/hypertour/' ) );
try {
  if ( JOINING_OPEN ) {
    window.sessionStorage.setItem( PARTICIPATING_KEY, '1' );
    window.sessionStorage.setItem( FOLLOWING_KEY, '1' );
  }
} catch ( _err ) {
  // A browser that keeps no storage still gets to the tour; it joins from
  // the row of faces when it arrives.
}

// Ask the tour where it is; work it out only if nothing answers.
//
// The guide is the canon, so the address it publishes beats any sum done here
// (spec/hyper-map/tour-state.md). The clock below stays as the fallback for a
// room with no guide in it — and it needs the map, because a programme built
// without the sheets runs a shorter cycle than the guide's and drifts a whole
// space apart over a lap.
const sheets = Promise.all(
  HYPER_TOUR_PROGRAM.map( ( stage ) => loadMapSheet( stage.space, stage.sheet ) ) );

const fromTheClock = () => sheets.then( () => {
  const program = new TourProgram( HYPER_TOUR_PROGRAM, {
    sheetFor: mapSheet,
    waypoints: ( stage, from, to ) =>
      waypointsBetween( mapSheet( stage.space ), from.position, to.position ),
  } );
  return program.sample( Date.now() ).stage;
} );

if ( JOINING_OPEN ) {
  askTheRoom()
    .then( ( space ) => ( space && stageForSpace( space ) ) || fromTheClock() )
    .then( ( stage ) => {
      if ( !stage ) return;
      markCrossing( stage.page );
      window.location.replace( withPageLang( stage.page ) );
    } );
}
